import type { BlogPost } from "./posts";

export const pkmPosts: BlogPost[] = [
  {
    slug: "find-hidden-connections-in-your-notes",
    title: "How to Find Hidden Connections in Your Notes",
    description:
      "A practical method for spotting the links between notes you already have: repetition, different words for the same idea, and tension, plus tools that help.",
    date: "2026-09-18",
    readTime: "7 min read",
    category: "Knowledge",
    preserveDate: true,
    content: `
## The Connections Are Usually Already There

Most people who keep notes for a while end up with the same complaint: there is a lot in there, but it doesn't add up to much. What is missing is the view across notes, the moment you notice that a problem from a customer call is the same problem you wrote about in a planning doc two weeks earlier.

You rarely need more notes to get there. The connections are usually already in what you wrote. They stay hidden because each note was written and read in its own context, and nobody has read them side by side.

This is a method for doing that on purpose. It works on paper or in any notes app, and the last section covers tools that help.

## Step 1: Put the Material Side by Side

You can't see a connection between two notes if you never look at them together. So start by choosing a scope, then gathering everything inside it into one view.

Good scopes are narrow and real:

- One project, from kickoff to now
- One month of meeting notes with the same team
- Every interview from one round of research
- Everything you have written about one question you keep coming back to

Then lay it out so you can see it all at once: one canvas, printouts on a table, or a split view. The rule is what matters: if you have to remember what another note said, you won't notice the connection.

## Step 2: Read Across, Not Down

Normal reading goes down a note from top to bottom. For this, read across notes instead. Pick one idea from the first note and ask: where else does this show up?

There are three signals worth looking for.

### Repetition

The same phrase in more than one place. A to-do copied from a meeting into a task list. A customer's exact wording that turns up in two interviews. A risk that appears in three different planning notes.

Repetition is the easiest signal to spot, and it tells you what keeps coming back. Something you wrote down three times in three contexts probably matters more than your notes currently treat it.

### Different words for the same idea

This is where most hidden connections live. One note says "churn", another says "people stop using it after the first week", a third says "retention". Same idea, different words, so a keyword search for any one of them misses the others.

When you find one of these clusters, give it a name. Pick one word and write it next to each instance. Naming is what turns a vague feeling ("I think I've seen this before") into something you can search, tag and link.

### Tension

Two notes that disagree. A goal in one note that another note quietly makes impossible. A decision in March that a customer call in May argues against.

Tension is the most valuable signal and the one no tool will find for you, because it depends on what the notes claim, not which words they use. For each cluster you found, ask whether all the notes in it point the same way.

## Step 3: Make the Connection Visible

A connection you noticed but didn't record is gone by next week. Write it down in a form you will find again:

- **Move things together.** On a canvas, drag the related notes next to each other. Proximity is the cheapest link there is.
- **Draw the line.** An arrow between two blocks, with a word or two on it saying why they are connected.
- **Write a linking note.** A short note that names the connection and says what you think it means. This is the core move in the [Zettelkasten method](/blog/productivity/zettelkasten-method-beginners-guide), and it is the step people skip most.
- **Link by name.** A wiki link or a shared tag, so the connection survives when the notes are far apart. [Connecting ideas with links and tags](/blog/guides/connecting-ideas-across-notes-linking-tagging) goes into how to choose between the two.

The linking note matters most. The other three record that a connection exists; the linking note records what you concluded from it, and that is the part that turns into [an evergreen note](/blog/knowledge/evergreen-notes-stay-useful) you can use again.

## Step 4: Repeat It on a Schedule

Doing this once is useful. Doing it regularly is what makes a notes system compound. A connection pass fits naturally into a [weekly review](/blog/productivity/weekly-review-that-works): take the notes you wrote that week, put them next to the notes on the same project, and spend ten minutes reading across.

Keep it small: the goal is the two or three connections that change what you do next.

## Where Tools Help (and Where They Don't)

Everything above works by hand. Tools help with the tedious part, which is finding repetition and near-repetition across a lot of text. None of them can do the judgment part, which is deciding whether a connection means anything.

Here is what the common tools are good at:

- **Full-text search** finds every note containing an exact word. Great once you know the word; no help with synonyms. See [search tips](/blog/tutorials/omnicanvas-search-tips).
- **Tags** group notes you have already decided belong together. They record connections rather than discover them. See [tagging systems that scale](/blog/knowledge/tagging-systems-that-scale).
- **Links, backlinks and graph views** show connections someone already made explicit. A [knowledge graph](/blog/tutorials/omnicanvas-knowledge-graph-guide) is useful for spotting clusters and orphans among them.

In OmniCanvas we added one more tool for the step 2 reading pass, called [concept lines](/blog/tutorials/concept-lines-see-how-ideas-connect). Turn on **Concepts** on a canvas and it highlights ideas that run through more than one text block or sticky note, and joins them with a colored line. It picks up repeated lines and shared words out of the box. With an optional 23 MB model that runs on your device, it also links different words with the same meaning, such as "revenue" and "sales", which is the second signal above.

It has limits worth knowing. It works within one canvas, not across your whole library. It shows up to eight concepts at a time. It does not appear on phones or windows narrower than 1,024 pixels. And like every tool on this list, it finds overlap, not tension. A line between two blocks tells you they share an idea; whether they agree is still for you to read.

That is the pattern with all of these tools: they handle the text matching and leave the thinking to you. That's the right split, because a connection only becomes useful once you have decided what it means.

## A Ten-Minute Version

If you only have ten minutes this week:

1. Pick one project.
2. Put its notes from the past two weeks side by side.
3. Find one phrase that shows up more than once, and one idea that shows up under two different names.
4. Write a three-sentence note about what that repetition tells you.
5. Link that note to the notes it came from.

That's it. Do it again next week.

## Frequently Asked Questions

### What is a hidden connection in notes?
It is a relationship between two or more notes that you haven't recorded yet: the same issue raised in different meetings, the same idea written in different words, or two notes that contradict each other. The notes already contain it; you just haven't read them side by side.

### How often should I look for connections?
A short pass once a week works well for most people, ideally as part of a weekly review. Keep it small and aim for two or three useful connections, not a complete map.

### Can software find connections in my notes automatically?
Partly. Search, graph views and features like OmniCanvas concept lines can find repeated words, shared phrases and, with a meaning model, different words for the same idea. They can't tell you whether two notes agree or conflict, or what a connection means for your work.

### What should I do once I find a connection?
Record it where you will find it again: move the notes together, draw a line, link them, and write a short note saying what the connection means. The written note is the part that pays off later.
`,
  },
  {
    slug: "what-is-personal-knowledge-management",
    title: "What Is Personal Knowledge Management (PKM)? A Practical Guide",
    description:
      "A comprehensive guide to PKM — what it is, why it matters, and the most popular systems (PARA, Zettelkasten, CODE). Practical getting-started advice.",
    date: "2026-07-26",
    readTime: "9 min read",
    category: "Knowledge",
    content: `
## Why You Need a System for What You Know

Every day you consume an enormous amount of information: articles, meetings, conversations, books, podcasts, and random flashes of insight in the shower. Without a deliberate system, most of that knowledge evaporates within days. Personal Knowledge Management (PKM) is the practice of capturing, organizing, and retrieving what you learn so it compounds over time instead of disappearing.

PKM is not a single app or a single method. It is a **habit layered on top of tools** — a commitment to treating your own knowledge as an asset worth managing. The goal is simple: when you need an idea, a fact, or a connection six months from now, you can find it in seconds rather than starting from scratch.

## What PKM Actually Looks Like in Practice

At its core, PKM involves four activities:

1. **Capture** — Saving information that resonates, whether it is a quote from a book, a diagram from a whiteboard, or a mental model you just learned.
2. **Organize** — Putting that information somewhere you can find it again using folders, tags, links, or spatial arrangement.
3. **Distill** — Refining raw captures into your own words so the ideas become truly yours.
4. **Express** — Using your knowledge to create something: a document, a presentation, a decision, or a new idea.

These four steps map directly to Tiago Forte's **CODE framework** (Capture, Organize, Distill, Express), one of the most popular PKM models. But CODE is not the only game in town.

## Three Popular PKM Systems

### PARA Method

Developed by Tiago Forte, PARA organizes everything into four top-level categories:

- **Projects** — Active efforts with a deadline (e.g., "Q3 product launch").
- **Areas** — Ongoing responsibilities with no end date (e.g., "Health," "Finances," "Team management").
- **Resources** — Topics of interest you may reference later (e.g., "Machine learning," "Cooking techniques").
- **Archives** — Inactive items from the other three categories.

PARA works well for action-oriented people because it ties knowledge directly to what you are working on right now. The downside is that it can feel rigid if your interests are fluid and exploratory.

### Zettelkasten

The Zettelkasten (German for "slip box") method was popularized by sociologist Niklas Luhmann, who used it to produce over 70 books and 400 academic papers. The core principles are:

- Write **atomic notes** — each note contains exactly one idea.
- Give each note a **unique identifier** so you can reference it from other notes.
- **Link notes together** to build a web of ideas that grows organically.

Zettelkasten rewards long-term thinkers. Because every note is small and linked, surprising connections emerge over time. The tradeoff is that it requires discipline to write well-formed atomic notes consistently.

### Spatial / Visual PKM

A newer approach arranges knowledge on an **infinite canvas** rather than in a file tree or outline. You place notes, images, and sketches on a two-dimensional surface and cluster related ideas visually. This mirrors how many people actually think — in spatial relationships rather than hierarchies.

Tools like OmniCanvas lean into this approach, letting you combine text, drawings, and imported content on a single surface — an [infinite canvas whiteboard](/tools/whiteboard) where ideas live in space. Spatial PKM is especially powerful for creative professionals and visual thinkers who find rigid folder structures limiting, and it pairs naturally with a [spatial second-brain workflow](/blog/productivity/how-to-build-a-second-brain-with-spatial-notes).

## How to Choose a System

There is no universally best PKM system. The right choice depends on your goals and thinking style:

- **If you are project-driven and need to stay organized across many responsibilities**, start with PARA.
- **If you are a researcher, writer, or lifelong learner who values deep connections between ideas**, try Zettelkasten.
- **If you think visually and want freedom to arrange ideas spatially**, explore a canvas-based approach.
- **If none of these feels right on its own**, combine elements. Many people use PARA for high-level organization and Zettelkasten-style linking within each area.

The most important rule is this: **any system you actually use beats a perfect system you abandon after a week.**

## Getting Started in 30 Minutes

Here is a simple way to begin today:

1. **Pick one tool.** Do not spend weeks evaluating software. Choose something you already have access to and start.
2. **Create a capture inbox.** This is a single place where every new piece of information lands before you organize it. It can be a folder, a tag, or a dedicated space on your canvas.
3. **Process your inbox weekly.** Once a week, go through your inbox. For each item, decide: organize it, distill it into a permanent note, or delete it.
4. **Write in your own words.** Copying and pasting a quote is not knowledge management. Rewriting an idea in your own language forces you to understand it deeply.
5. **Link and connect.** Whenever you create a new note, spend 60 seconds asking: what existing notes does this relate to? Add a link, a tag, or move it near related ideas on your canvas.

## Common Mistakes to Avoid

- **Over-engineering your system before you have any notes.** Start simple and add structure as needed.
- **Treating your PKM as a write-only archive.** If you never revisit your notes, the system has no value. Schedule regular review sessions.
- **Capturing everything.** Be selective. Not every article or tweet deserves a place in your knowledge base. Save only what genuinely resonates or what you expect to use.
- **Switching tools constantly.** Tool-hopping feels productive but destroys momentum. Commit to one tool for at least three months before evaluating alternatives.

## The Long Game

PKM is a compounding investment. The first month feels slow — you are building infrastructure with little payoff. By month six, you start finding notes you forgot you wrote and making connections that surprise you. By year two, your knowledge base becomes a genuine thinking partner that accelerates everything you do.

The best time to start was years ago. The second best time is today.
`,
  },
  {
    slug: "evergreen-notes-stay-useful",
    title: "Evergreen Notes: How to Write Notes That Stay Useful Forever",
    description:
      "The concept of evergreen/atomic notes — self-contained, well-written notes that remain valuable over time rather than decaying.",
    date: "2026-08-02",
    readTime: "7 min read",
    category: "Knowledge",
    content: `
## The Problem With Most Notes

Open your notes app right now and scroll back six months. How many of those notes make sense to you today? If you are like most people, the answer is discouraging. You will find cryptic bullet points, half-finished thoughts, and meeting notes that lost all context the moment the meeting ended.

This is the **note decay problem**. Most notes are written for your present self — someone who has full context and remembers why the note matters. Your future self is essentially a stranger reading someone else's shorthand.

Evergreen notes solve this problem by design.

## What Makes a Note Evergreen

The term "evergreen notes" was popularized by Andy Matuschak, building on ideas from the Zettelkasten tradition. An evergreen note has four key properties:

### 1. It Is Atomic

Each note contains **one idea and one idea only**. If you find yourself writing "also" or "another thing" in the same note, that is a signal to split it into two separate notes.

Atomic notes are easier to link, easier to find, and easier to reuse. A note titled "Three things I learned from the sales kickoff" is hard to reference later. Three separate notes — each about one lesson — are individually useful in different contexts.

### 2. It Is Written in Complete Sentences

Bullet-point shorthand feels efficient in the moment but becomes unreadable over time. Evergreen notes use **full prose** that your future self can understand without any additional context.

Compare these two versions:

**Fragile note:** "Pareto - 80/20 - focus on vital few - see also: Tim F podcast"

**Evergreen note:** "The Pareto Principle states that roughly 80 percent of outcomes come from 20 percent of inputs. In personal productivity, this means identifying the small number of tasks that generate disproportionate results and protecting time for those tasks above all else."

The second version will still make perfect sense in five years. The first will not.

### 3. It Is Concept-Oriented, Not Source-Oriented

Most people organize notes by source: "Notes from Book X" or "Meeting notes 2026-03-01." The problem is that a single book or meeting contains dozens of unrelated ideas. When you need one of those ideas later, you have to dig through an entire document to find it.

Evergreen notes are organized around **concepts**, not sources. Instead of "Notes from Thinking Fast and Slow," you create individual notes like "System 1 thinking operates automatically and quickly" and "Anchoring bias causes people to rely too heavily on the first piece of information they encounter."

You can still record which source the idea came from — just do it as a reference at the bottom of the concept note, not as the organizing principle.

### 4. It Is Densely Linked

An evergreen note does not exist in isolation. It connects to other notes through explicit links, tags, or — in spatial tools like OmniCanvas — visual proximity on a [canvas](/tools/whiteboard). These connections are what transform a collection of notes into a **thinking tool**.

Every time you write a new evergreen note, spend a moment asking: what existing notes does this relate to? What does it support, contradict, or extend? Adding even one or two connections per note creates a web that grows exponentially more valuable over time.

## How to Write Evergreen Notes in Practice

### Start With a Clear Title

The title of an evergreen note should be a **declarative statement**, not a topic label. "Distributed teams" is a topic. "Distributed teams require more explicit communication than co-located teams" is a claim you can engage with, challenge, and build on.

Good titles act as a compression of the note itself. You should be able to scan a list of titles and recall the core argument of each note without opening it.

### Use the Capture-Then-Refine Workflow

You do not have to write perfect evergreen notes in real time. Instead, use a two-step process:

1. **Capture quickly.** During a meeting, lecture, or reading session, jot down rough notes in whatever format is fastest. Do not worry about quality.
2. **Refine later.** During a dedicated processing session — daily or weekly — review your rough captures and convert the best ideas into well-written evergreen notes.

This separation means you never slow down your capture speed, but you also never leave rough notes to rot.

### Be Ruthlessly Selective

Not every idea deserves an evergreen note. The goal is a curated collection of your best thinking, not a comprehensive archive of everything you have ever encountered. A good rule of thumb: if you cannot imagine wanting to revisit an idea in six months, do not promote it to an evergreen note.

## The Compounding Payoff

Evergreen notes become dramatically more valuable over time because of their connections. When you write your 50th note, it does not just add one unit of value — it potentially connects to dozens of existing notes, creating new pathways through your knowledge.

Writers find that article ideas emerge from clusters of linked notes. Researchers discover unexpected parallels between fields. Professionals build a personal reference library that makes them faster and more effective every year, building toward a [second brain](/blog/productivity/how-to-build-a-second-brain-with-spatial-notes) that compounds.

## One Note at a Time

The most common mistake is trying to convert an entire backlog of messy notes into evergreen format. Do not do this. Instead, start today: take one idea that matters to you and write a single, well-crafted evergreen note. Tomorrow, write another. The habit is more important than the volume.

Over months, those individual notes will weave into something far greater than the sum of their parts — a living, evolving map of your best thinking.
`,
  },
  {
    slug: "build-personal-wiki-career",
    title: "How to Build a Personal Wiki for Your Career",
    description:
      "How professionals can create and maintain a personal wiki of processes, contacts, lessons learned, and domain expertise.",
    date: "2026-08-09",
    readTime: "8 min read",
    category: "Knowledge",
    content: `
## Your Career Knowledge Is Leaking

Think about everything you have learned in your career: the unwritten rules of your industry, the processes that actually work, the contacts who can unblock any situation, the mistakes that taught you expensive lessons. Now ask yourself: where does all of that knowledge live?

For most professionals, the answer is "in my head, sort of." That is a fragile system. Memory is unreliable, context-dependent, and non-transferable. When you switch roles, start a new project, or simply have a busy quarter, critical knowledge slips away.

A **personal wiki** is a structured, searchable knowledge base that captures your professional expertise so it compounds over your entire career rather than resetting with every job change.

## What Belongs in a Career Wiki

Your personal wiki should cover five core areas:

### 1. Processes and Playbooks

Document the repeatable processes you use or have developed. These might include:

- How you run a project kickoff meeting
- Your checklist for reviewing a contract or proposal
- Steps for onboarding a new team member
- Your process for preparing a quarterly business review

Write these as step-by-step instructions that your future self (or a colleague) could follow without additional context. Include the **why** behind each step, not just the what — this makes the playbook adaptable to new situations.

### 2. People and Relationships

Maintain a lightweight CRM for your professional network:

- Name, role, company, and how you met
- Key interests or expertise areas
- Notes from recent conversations
- When you last reached out

This is not about being transactional. It is about being a better colleague and collaborator. When someone mentions a problem, you can quickly search your wiki and say, "I know someone who has solved exactly this — let me connect you."

### 3. Lessons Learned

After every significant project, decision, or mistake, write a brief retrospective note:

- **What happened** — the situation and context
- **What went well** — tactics and decisions worth repeating
- **What went wrong** — mistakes and their root causes
- **What I would do differently** — concrete changes for next time

These notes are gold when you face a similar situation in the future. Instead of repeating old mistakes, you can review your own hard-won wisdom in minutes.

### 4. Domain Expertise

Capture the specialized knowledge that makes you valuable in your field:

- Technical concepts and how they connect
- Industry trends and your analysis of them
- Frameworks and mental models you rely on
- Definitions of jargon that confuses newcomers

This is where your wiki becomes a genuine competitive advantage. Over years, this section transforms into a comprehensive reference that would take a newcomer months to build from scratch.

### 5. Templates and Artifacts

Save reusable templates for documents you create regularly:

- Email templates for common situations (introductions, follow-ups, negotiations)
- Presentation structures that have worked well
- Document outlines for reports, proposals, or briefs
- Code snippets or configuration patterns you reuse

Templates save time, but more importantly, they encode your best practices so your quality stays consistently high even when you are rushed.

## Structuring Your Wiki

### Start With a Home Page

Create a single entry point that links to your major sections. This is your table of contents and your most frequently visited page. Keep it clean and update it as your wiki grows.

### Use a Flat Hierarchy With Tags

Deep folder nesting kills wikis. You create a folder structure, forget where you put things, and stop adding new entries. Instead, keep your hierarchy shallow — no more than two levels deep — and rely on **tags** for cross-cutting organization.

For example, a lesson learned from a product launch might be tagged with "product," "launches," "mistakes," and "stakeholder-management." You can find it from any of those angles without remembering which folder you filed it in.

### Name Pages for Retrieval, Not Filing

A page titled "Q2 Notes" is useless six months later. A page titled "How to handle scope creep in client projects" is immediately findable and understandable. Write page titles as if you are searching for them, because you will be.

## Tools and Setup

You can build a personal wiki in almost any tool that supports search, linking, and some form of organization. The key requirements are:

- **Fast search** so you can find anything in seconds
- **Linking between pages** so related knowledge stays connected
- **Low friction for adding new entries** so you actually maintain it
- **Portability** so your wiki survives tool changes and job changes

A spatial tool like OmniCanvas can work particularly well for career wikis because you can arrange related topics on an [infinite canvas](/tools/whiteboard) — clustering your product knowledge in one area, leadership lessons in another, and technical references in a third. The visual layout provides an additional retrieval cue that pure text search cannot match.

## The Weekly Maintenance Habit

A wiki that is not maintained becomes a graveyard. Build a simple weekly habit:

1. **Spend 15 minutes every Friday** reviewing what you learned that week.
2. **Add one or two new entries** — a process you refined, a lesson from a tough conversation, a contact you made.
3. **Update one existing entry** — add a detail, correct something outdated, or link it to a newer note.

Fifteen minutes a week is roughly 13 hours a year. That is a tiny investment for a knowledge base that will serve you for decades.

## The Career Compound Effect

Most professionals start each new role from near zero, relying on memory and instinct. A personal wiki changes the equation entirely. Every role, project, and mistake enriches your wiki, and every new challenge benefits from everything you have already learned.

After a year, your wiki saves you hours of reinvention. After five years, it becomes an irreplaceable professional asset — a [second brain](/blog/productivity/how-to-build-a-second-brain-with-spatial-notes) that remembers everything your first brain cannot.
`,
  },
  {
    slug: "tagging-systems-that-scale",
    title: "Tagging Systems That Scale: From 10 Notes to 10,000",
    description:
      "How to design a tagging system that works at scale, including tag hierarchies, naming conventions, and when to tag vs. folder.",
    date: "2026-08-16",
    readTime: "7 min read",
    category: "Knowledge",
    content: `
## Why Most Tagging Systems Fall Apart

Tags seem simple at first. You create a few — "work," "ideas," "books" — and everything feels tidy. Then six months pass and you have 200 tags, half of which are duplicates ("meeting-notes" vs. "meetings" vs. "meeting"), and clicking any single tag returns so many results that it is useless for finding anything specific.

This is the **tag sprawl problem**, and it kills more personal knowledge systems than any other organizational failure. The good news is that tag sprawl is preventable with a few deliberate design choices made early.

## Principle 1: Tags Describe Properties, Folders Describe Identity

The most common tagging mistake is using tags as substitutes for folders. Tags and folders serve fundamentally different purposes:

- **Folders** answer "What is this?" A note about a project kickoff belongs in the project folder. It has one primary identity.
- **Tags** answer "What properties does this have?" That same note might be tagged "template," "leadership," and "Q3" because it has multiple cross-cutting properties.

When you use tags for identity ("this is a meeting note"), you end up recreating a folder structure with tags, which is the worst of both worlds. Reserve tags for properties that cut across categories.

## Principle 2: Use a Controlled Vocabulary

A **controlled vocabulary** means you define your tags in advance rather than inventing them on the fly. This does not mean your tag list is frozen forever — it means you add new tags deliberately rather than impulsively.

Keep a master tag list somewhere visible. Before creating a new tag, check the list:

- Does an existing tag already cover this concept?
- Is this tag specific enough to be useful but broad enough to apply to more than three notes?
- Does the tag name follow your naming convention?

If you cannot answer yes to all three, do not create the tag.

## Principle 3: Naming Conventions Matter

Inconsistent naming is the fastest path to tag sprawl. Pick conventions and enforce them ruthlessly:

**Format rules:**
- Use lowercase only: "productivity" not "Productivity"
- Use hyphens for multi-word tags: "mental-models" not "mental models" or "mentalmodels"
- Use singular form: "book" not "books"
- Avoid abbreviations unless they are universally understood in your context

**Prefixes for clarity:**
Prefixes create lightweight hierarchies without requiring your tool to support nested tags:

- "type/" for content types: type/article, type/template, type/meeting-note
- "project/" for project-specific tags: project/website-redesign, project/q3-launch
- "status/" for workflow states: status/draft, status/review, status/complete
- "source/" for where information came from: source/book, source/podcast, source/conversation

Prefixes make your tag list scannable and self-documenting. When you see "type/template" you immediately know what it means without any additional context.

## Principle 4: Limit Your Tag Count

A useful guideline: aim for **no more than 3-5 tags per note** and **no more than 50-75 active tags total** in your system. If you exceed these limits, you are almost certainly duplicating concepts or tagging at too fine a grain.

When your tag count creeps up, schedule a pruning session:

1. Sort tags by usage count
2. Merge near-duplicates (pick one canonical name and retag)
3. Delete tags used fewer than three times
4. Demote overly specific tags into note content rather than tags

## Principle 5: Tags Should Enable Useful Intersections

The real power of tags is not in any single tag — it is in **combinations**. A good tagging system lets you ask questions like:

- Show me all templates related to leadership (type/template + leadership)
- What lessons have I learned from client projects? (lesson-learned + client)
- Which book notes relate to decision-making? (source/book + decision-making)

Design your tags with intersections in mind. If two tags would always appear together, merge them into one. If a tag only ever appears alone and never in useful combinations, it might be better as a folder.

## When Spatial Organization Replaces Tags

Tags are one solution to the "find it later" problem, but they are not the only one. In spatial tools like OmniCanvas, **visual clustering** serves a similar function. You can group related notes in a region of your [canvas](/tools/whiteboard), and the spatial position itself becomes metadata.

Spatial organization works best for smaller, actively-used collections where you want to see relationships at a glance. Tags work best for larger archives where you need precise search and filtering. Many people use both: spatial arrangement for active thinking spaces and tags for long-term retrieval — the same blend that powers a [spatial second brain](/blog/productivity/how-to-build-a-second-brain-with-spatial-notes). For more on weaving notes together, see [connecting ideas across notes with linking and tagging](/blog/guides/connecting-ideas-across-notes-linking-tagging).

## A Starter Tag System

If you are setting up tags from scratch, here is a minimal starting point you can adapt:

**Content types (5-7 tags):**
- type/note, type/template, type/meeting-note, type/lesson-learned, type/reference, type/idea

**Status (3-4 tags):**
- status/inbox, status/active, status/archive

**Domains (adapt to your life, 5-10 tags):**
- Whatever your major areas of responsibility are: engineering, marketing, health, finance, leadership, etc.

That gives you roughly 15-20 tags to start. Add new ones only when you feel a genuine gap — when you search for something and realize no existing tag would have helped you find it.

## Tags Are a Garden, Not Architecture

The final mindset shift: think of your tagging system as a **garden** that needs regular tending, not as architecture that must be designed perfectly upfront. Prune dead tags quarterly. Merge duplicates when you spot them. Add new tags when genuine needs arise. A living, evolving tag system will always outperform a rigid one.
`,
  },
  {
    slug: "take-notes-on-podcasts-videos",
    title: "How to Take Notes on Podcasts and Videos",
    description:
      "Techniques for capturing insights from audio and video: timestamp notes, progressive summarization, and integration with a PKM system.",
    date: "2026-08-23",
    readTime: "6 min read",
    category: "Knowledge",
    content: `
## The Audio and Video Note-Taking Problem

Podcasts and videos are among the richest sources of ideas available today. Long-form conversations surface insights that rarely appear in written articles. The problem is that audio and video are **linear and ephemeral** — you cannot skim them, you cannot search them easily, and the moment a great idea flies by, it is gone unless you captured it.

Most people solve this by doing nothing. They listen, nod along, and forget 90 percent of what they heard within a week. The remaining 10 percent who do take notes often create messy transcripts that are barely more useful than not taking notes at all.

There is a better way.

## The Timestamp Method

The simplest and most effective technique for audio/video notes is **timestamp noting**. Here is how it works:

1. Keep a note open alongside whatever you are watching or listening to.
2. When you hear something valuable, jot down the **timestamp** and a **one-sentence summary** of the idea.
3. Do not try to capture everything. Aim for 5-10 timestamps per hour of content.

A timestamp note for a one-hour podcast might look like this:

- 04:30 — The guest explains why writing clarifies thinking more than talking does
- 12:15 — Interesting framework: separate "capture mode" from "organize mode" to reduce friction
- 28:40 — Story about how a daily 10-minute review habit transformed their productivity
- 41:00 — Distinction between "just in case" and "just in time" learning
- 55:20 — Recommended practice: write a one-paragraph summary immediately after finishing any learning session

This gives you a **navigable index** into the content. When you want to revisit an idea months later, you can jump directly to the relevant moment instead of re-listening to the entire episode.

## Progressive Summarization for Media

Tiago Forte's progressive summarization technique adapts beautifully to audio and video notes. The process has three layers:

**Layer 1: Timestamp notes (during consumption).** Capture timestamps and brief summaries as described above. This takes minimal effort and does not interrupt your listening flow.

**Layer 2: Highlight the best ideas (same day).** After finishing, reread your timestamp notes and **bold the most important points** — the ideas you want to remember most. This usually reduces your notes by about half.

**Layer 3: Write an executive summary (within a week).** In two to four sentences, write the core takeaway of the entire piece in your own words. This is the note you will actually read when you revisit the content in the future.

Most content only needs layers 1 and 2. Reserve layer 3 for the episodes and videos that genuinely changed your thinking.

## When to Take Notes (and When Not To)

Not every podcast or video deserves notes. Taking notes during casual entertainment adds friction without value. Here is a simple decision framework:

**Take detailed notes when:**
- You are listening specifically to learn about a topic
- The speaker shares frameworks, mental models, or actionable advice
- You plan to write about or act on the ideas

**Take light notes (just a few timestamps) when:**
- The content is interesting but not directly actionable
- You want to remember one or two key points
- You might want to reference it in a future conversation

**Skip notes entirely when:**
- You are listening for entertainment or relaxation
- The content is not relevant to any area you are actively developing

Being selective about when to take notes protects both your enjoyment of media and the quality of your knowledge base.

## Integrating Media Notes Into Your PKM System

Timestamp notes are only the first step. The real value comes when you **integrate the best ideas into your broader knowledge system** — the foundation of a [second brain](/blog/productivity/how-to-build-a-second-brain-with-spatial-notes).

After processing a podcast or video, ask yourself:

- Did any idea connect to an existing note or project? If so, add a link or place the new note near the related content in your workspace.
- Did any idea deserve to become a standalone evergreen note? If a concept is important enough to reference independently, extract it from the media note and write it up properly.
- Did the content suggest an action item? If so, move it to your task system immediately rather than leaving it buried in notes.

In a spatial tool like OmniCanvas, you might place your media notes in a dedicated "inbox" region of your [canvas](/tools/whiteboard), then drag the best ideas into the relevant areas of your knowledge map during your weekly review.

## Practical Tips for Different Contexts

### Listening While Commuting or Exercising

You cannot write while driving or running. Use your phone's voice memo app to record a quick verbal note — just the timestamp and the idea in a few words. Process these voice captures when you are back at your desk.

Alternatively, most podcast apps let you save clips or add bookmarks. Use these as lightweight capture points and review them later.

### Watching Video Courses or Lectures

Video courses warrant more detailed notes because you are explicitly learning. Pause after each major section and write a brief summary before moving on. This **retrieval practice** dramatically improves retention compared to passive watching.

### Live Webinars and Presentations

For live content you cannot pause, default to the timestamp method with rapid shorthand. Focus on capturing the key ideas rather than complete sentences. Clean up your notes within 24 hours while the context is still fresh.

## The 24-Hour Rule

The single most important habit for media note-taking is processing your captures **within 24 hours**. Raw timestamp notes lose context quickly. A note that says "28:40 — interesting framework about feedback" will make perfect sense today but will be meaningless next month.

Within 24 hours, review your timestamps, bold the highlights, and for the best ideas, write a sentence or two of context in your own words. This small investment transforms throwaway jottings into lasting knowledge.
`,
  },
  {
    slug: "save-organize-web-clippings",
    title: "How to Save and Organize Web Clippings That You'll Actually Revisit",
    description:
      "Stop saving articles you never read again. A system for curating, annotating, and integrating web clippings into your knowledge base.",
    date: "2026-08-30",
    readTime: "7 min read",
    category: "Knowledge",
    content: `
## The Bookmarking Graveyard

Be honest: how many articles, threads, and web pages have you saved with the vague intention of "reading later"? If you are like the average knowledge worker, the number is in the hundreds, and the percentage you have actually revisited is close to zero.

This is the **collector's fallacy** — the feeling that saving information is the same as learning it. Your bookmarks folder, read-later app, and browser tabs are full of good intentions and zero value. The problem is not that you save too much. The problem is that **saving is the end of your workflow instead of the beginning**.

A good web clipping system turns saving into a first step that leads reliably to reading, annotating, and integrating the best ideas into your knowledge base, feeding a [second brain](/blog/productivity/how-to-build-a-second-brain-with-spatial-notes) instead of a bookmark graveyard.

## Step 1: Triage Before You Save

The first change is behavioral: stop saving everything that looks interesting. Instead, apply a **quick triage** before you clip:

- **Will I need this for a specific project or decision in the next two weeks?** Save it immediately and tag it with the relevant project.
- **Does this contain a genuinely new idea that I want to learn from?** Save it to your reading inbox.
- **Is this just mildly interesting?** Do not save it. If it is truly important, it will cross your path again.

This filter alone eliminates 50-70 percent of the clutter in most people's clipping systems. Being selective at the point of capture is far more effective than organizing a mountain of mediocre saves later.

## Step 2: Create a Reading Inbox With a Capacity Limit

Everything you save should land in a single **reading inbox** — not scattered across bookmarks, tabs, email, and three different apps. One inbox, one place to check.

Here is the critical addition: give your inbox a **capacity limit**. When it reaches 20 items (or whatever number you choose), you must process it before adding anything new. This creates healthy pressure to actually read what you have saved rather than endlessly accumulating.

Processing means one of three actions for each item:

1. **Read and annotate** — Read the article, highlight the key passages, and write a brief summary in your own words.
2. **Skim and extract** — If the full article is not worth your time, pull out the one or two useful ideas and discard the rest.
3. **Delete** — If it no longer seems relevant or interesting, remove it without guilt. Your priorities change, and that is fine.

## Step 3: Annotate With Purpose

The difference between a useful clipping and a dead bookmark is **annotation**. When you read a saved article, do not just highlight passively. Engage with the text:

- **Highlight sparingly.** If you highlight everything, you have highlighted nothing. Limit yourself to 3-5 key passages per article.
- **Write margin notes.** Next to each highlight, write why it matters to you. "This contradicts what I learned about pricing in the SaaS article from last month" is infinitely more useful than a yellow highlight with no context.
- **Summarize in one paragraph.** After reading, write a brief summary at the top of the clipping: what is the core argument, and what is your take on it?

This annotation process forces you to think critically about what you read, which is where the actual learning happens.

## Step 4: Integrate Into Your Knowledge Base

Annotated clippings should not stay in your reading inbox forever. The final step is **integration** — connecting the best ideas to the rest of your knowledge.

This can take several forms:

- **Create an evergreen note.** If the article contains a concept worth remembering long-term, write a standalone note in your own words and file it in your knowledge system. The clipping becomes a source reference, not the primary artifact.
- **Link to existing notes.** If the article supports or challenges something you have already written about, add a connection. In a spatial workspace like OmniCanvas, you might place the clipping near related notes on your [canvas](/tools/whiteboard) so the relationship is visually obvious.
- **Feed a project.** If the article is relevant to an active project, move the key insights into your project notes where you will actually use them.
- **Archive or delete the clipping.** Once you have extracted the value, the original clipping can go to an archive or be deleted entirely. The knowledge now lives in your notes, not in the clipping.

## A Weekly Clipping Routine

Without a regular habit, even the best system collapses. Here is a minimal weekly routine that takes about 30 minutes:

**Monday (5 minutes):** Review your reading inbox. Delete anything that no longer interests you. Prioritize the 3-5 items you will read this week.

**Throughout the week (15-20 minutes total):** Read and annotate your prioritized items during natural downtime — commuting, waiting, or winding down in the evening.

**Friday (5-10 minutes):** Integrate the best ideas into your knowledge base. Create evergreen notes, link to existing content, or feed project notes. Clear processed items from your inbox.

This rhythm ensures that your clipping system stays lean and that saved articles actually become knowledge.

## Choosing Your Tools

The specific tools matter less than the workflow, but there are a few qualities to look for:

- **One-click saving** from your browser so the capture step has near-zero friction
- **Highlighting and annotation** built into the reading experience
- **Easy export** so you can move ideas into your primary knowledge tool without manual copying
- **Search** across your clippings for those moments when you vaguely remember an article but cannot find it

The most important quality is that your clipping tool connects to wherever you do your real thinking. A clipping that stays in a separate app, disconnected from your notes and projects, is a clipping that will be forgotten.

## Quality Over Quantity

The ultimate measure of a web clipping system is not how many articles you save — it is how many saved articles actually change your thinking or improve your work. A system that saves 5 articles a week and deeply processes all of them will serve you far better than one that saves 50 and processes none.

Save less. Read more carefully. Annotate with purpose. Integrate the best ideas. Delete the rest without guilt. Your future self will thank you for a knowledge base built on quality rather than volume.
`,
  },
];
